import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchThemeData } from "../../admin/themeSlice";
import { selectAdminToken } from "../../admin/sessionSlice";
import { setBackButtonUrl } from "../uiSlice";
import "./game.css";
import { fetchReport } from "../../functions/fetchReport";
import { sendReport, extractReportIdFromResponse } from "../../functions/sendReport";
import Swal from "sweetalert2";

import {
  buildThemeAssetUrl,
  checkImageExists,
  cssBackgroundImageUrl,
  resolveImpactSoundUrl,
  resolveThemeBgMusicUrl,
  resolveThemeSpriteUrl,
  shouldUseMobileBackground,
} from "./bugGame/themeAssets";
import { normalizeBugCatalog, pickWeightedBug } from "./bugGame/bugCatalog";
import {
  comboMultiplierForStreak,
} from "./bugGame/scoring";

import localImpactSound from "../../assets/sound/impact.mp3";

import splashGif1 from "./splash/1.gif";
import splashGif2 from "./splash/2.gif";
import splashGif3 from "./splash/3.gif";
import splashGif4 from "./splash/4.gif";
import splashGif5 from "./splash/5.gif";
import splashGif6 from "./splash/6.gif";
import splashGif7 from "./splash/7.gif";

import popupGood from "./popup/Good.png";
import popupNice from "./popup/Nice.png";
import popupOnFire from "./popup/OnFire.png";
import popupInsane from "./popup/Insane.png";

const TIMER_STORAGE_KEY = "bug_smasher_timer_state_v2";
const REPORT_SENT_STORAGE_KEY = "bug_smasher_report_sent_v2";
const MAX_LIVE_BUGS = 26;
const SHAKE_MS = 380;
const COMBO_PULSE_MS = 260;
const FLOAT_POINTS_MS = 820;
const FALL_ACCEL = 5.5;
// Margins are in normalized playfield space (0..1).
// Keep separate values so bugs can spawn just offscreen, but only despawn once fully out.
const SPAWN_OFF_MARGIN = 0.12;
const DESPAWN_OFF_MARGIN = 0.26;

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

const safeStr = (v) => String(v ?? "").trim();

const buildMissingThemeAssetList = (themeData) => {
  const td = themeData || {};
  const missing = [];

  // Theme-level images (uploaded via Update Theme Images in admin).
  ([
    { key: "themeImage", label: "Thumbnail image" },
    { key: "logo", label: "Logo image" },
    { key: "background_desk", label: "Desktop background" },
    { key: "background_mob", label: "Mobile background" },
  ]).forEach(({ key, label }) => {
    if (!safeStr(td[key])) missing.push(`Theme: upload ${label}`);
  });

  // Gameplay bug media.
  const good = Array.isArray(td.good) ? td.good : [];
  const bad = Array.isArray(td.bad) ? td.bad : [];
  if (!good.length) missing.push("Gameplay: add at least 1 good bug");
  if (!bad.length) missing.push("Gameplay: add at least 1 bad bug");

  const checkBugRows = (rows, typeLabel) => {
    rows.forEach((row, idx) => {
      const gif = safeStr(row?.bug_gif ?? row?.bugGif);
      const death = safeStr(row?.death_image ?? row?.deathImage);
      if (!gif) missing.push(`${typeLabel} bug ${idx + 1}: upload Bug GIF`);
      if (!death) missing.push(`${typeLabel} bug ${idx + 1}: upload Death image (PNG)`);
    });
  };
  checkBugRows(good, "Good");
  checkBugRows(bad, "Bad");

  return missing;
};

/**
 * Bug Smasher gameplay always uses the theme's countdown timer (`themeData.timer`).
 * We intentionally ignore `use_timeout` here (some themes use it for other screens/flows).
 */
const themeUsesCountdown = () => true;

const getTimerSettings = (td) => {
  if (!td) return { totalSeconds: 90, useCountdown: true };
  const timerValue = td.timer || "05:00";
  const [minutes, seconds] = timerValue.split(":").map(Number);
  const totalSeconds = (minutes || 0) * 60 + (seconds || 0);
  return { totalSeconds, useCountdown: themeUsesCountdown(td) };
};

const formatClock = (totalSecs) => {
  const safe = Math.max(0, Math.floor(totalSecs));
  const m = Math.floor(safe / 60);
  const s = safe % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

const readPersistedTimer = () => {
  try {
    const raw = sessionStorage.getItem(TIMER_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch {
    return null;
  }
};

const writePersistedTimer = (payload) => {
  try {
    sessionStorage.setItem(TIMER_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* ignore */
  }
};

const clearPersistedTimer = () => {
  try {
    sessionStorage.removeItem(TIMER_STORAGE_KEY);
  } catch {
    /* ignore */
  }
};

const readLastReportSignature = () => {
  try {
    return sessionStorage.getItem(REPORT_SENT_STORAGE_KEY) || "";
  } catch {
    return "";
  }
};

const writeLastReportSignature = (signature) => {
  try {
    if (!signature) {
      sessionStorage.removeItem(REPORT_SENT_STORAGE_KEY);
      return;
    }
    sessionStorage.setItem(REPORT_SENT_STORAGE_KEY, signature);
  } catch {
    /* ignore */
  }
};

const soundEnabledFromTheme = (td) => {
  if (!td) return true;
  const v = td.sound_enabled ?? td.soundEnabled;
  return !(v === false || v === "false" || v === 0 || v === "0");
};

function createSessionState() {
  return {
    bugs: new Map(),
    nextBugId: 1,
    spawnAcc: 0,
    combo: 0,
    lastHitWallMs: 0,
    chainWallStartMs: 0,
    sumHitScores: 0,
    multSum: 0,
    comboBonusPoints: 0,
    comboBonusCount: 0,
    streakBonusPoints: 0,
    streakBonusCount: 0,
    goodHits: 0,
    badHits: 0,
    maxCombo: 0,
    hitsByBugType: {},
    escapedBugs: 0,
    totalTaps: 0,
    successfulHits: 0,
    sessionWallStartMs: 0,
  };
}

const Game = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { data: themeData, status: themeStatus } = useSelector((state) => state.theme);
  const { status, user } = useSelector((state) => state.auth);

  /** `null` until theme timer is initialized from config or sessionStorage (avoids flash end at 0). */
  const [timeLeft, setTimeLeft] = useState(null);
  const [score, setScore] = useState(0);
  const [combo, setCombo] = useState(0);
  const [goodHits, setGoodHits] = useState(0);
  const [badHits, setBadHits] = useState(0);
  const [maxComboHud, setMaxComboHud] = useState(0);
  const [hitPointPopups, setHitPointPopups] = useState([]);
  const [existingBestScore, setExistingBestScore] = useState(null);
  const [existingReportId, setExistingReportId] = useState(null);
  const [rulesLegendUrls, setRulesLegendUrls] = useState({ good: [], bad: [] });
  const [isMobile, setIsMobile] = useState(() => shouldUseMobileBackground());
  const [assetsReady, setAssetsReady] = useState(false);
  const [themeAssetsOk, setThemeAssetsOk] = useState(true);
  const [comboPulse, setComboPulse] = useState(false);
  const [screenShake, setScreenShake] = useState(false);
  const [onFirePulse, setOnFirePulse] = useState(false);
  const [centerPopup, setCenterPopup] = useState(null);
  const [showRules, setShowRules] = useState(false);
  const [saveState, setSaveState] = useState("idle");
  const [saveErrorDetail, setSaveErrorDetail] = useState("");

  const playfieldRef = useRef(null);
  const sessionRef = useRef(createSessionState());
  const playActiveRef = useRef(false);
  const rafRef = useRef(null);
  const lastFrameRef = useRef(null);
  const catalogRef = useRef({ good: [], bad: [], all: [] });
  const catalogSpawnMetaRef = useRef({ allAvg: 1, badAvg: 1, goodAvg: 1 });
  const urlCacheRef = useRef(new Map());
  const splashListRef = useRef([splashGif1, splashGif2, splashGif3, splashGif4, splashGif5, splashGif6, splashGif7]);
  const gameOverHandledRef = useRef(false);
  const popupTimersRef = useRef({ hide: 0, onFire: 0 });
  const themeDataRef = useRef(null);

  const hitAudioRef = useRef(null);
  const hitAudioPrimedRef = useRef(false);
  const hitAudioPrimeGenRef = useRef(0);
  const hitBeepCtxRef = useRef(null);
  const bgMusicRef = useRef(null);
  const hitPointsTimersRef = useRef({});

  const timerSettings = getTimerSettings(themeData);
  const { totalSeconds, useCountdown } = timerSettings;
  // Scope timer restore to the current player session so a previous player's finished game
  // doesn't immediately end a fresh player's run.
  const timerConfigKey = `${String(user?.sessionId || "anon")}::${themeData?.themename || "default"}|${
    themeData?.timer || "05:00"
  }|${useCountdown ? 1 : 0}`;
  const hasThemeLoaded = Boolean(themeData);

  const timerReady = timeLeft !== null;

  const isSessionOver =
    hasThemeLoaded &&
    timerReady &&
    (useCountdown ? timeLeft <= 0 : timeLeft >= totalSeconds && totalSeconds > 0);

  const isPlaying =
    hasThemeLoaded &&
    timerReady &&
    !isSessionOver &&
    saveState !== "saving" &&
    assetsReady &&
    themeAssetsOk;

  const elapsedSeconds = !timerReady
    ? 0
    : useCountdown
      ? Math.max(0, totalSeconds - timeLeft)
      : Math.max(0, timeLeft);

  const impactSoundSrc = useMemo(() => resolveImpactSoundUrl(themeData), [themeData]);
  const bgMusicSrc = useMemo(() => resolveThemeBgMusicUrl(themeData), [themeData]);

  const gamePageBgUrl = useMemo(() => {
    if (!themeData) return "";
    const key = isMobile ? themeData.background_mob : themeData.background_desk;
    const raw = String(key || themeData.bg_image || "").trim();
    return raw ? buildThemeAssetUrl(raw) : "";
  }, [themeData, isMobile]);

  const currentMultiplierLabel = useMemo(() => {
    const m = comboMultiplierForStreak(Math.max(0, combo));
    const label = Number.isInteger(m) ? String(m) : String(m);
    return `×${label}`;
  }, [combo]);

  const resolveCachedUrl = useCallback(async (raw) => {
    const key = String(raw || "").trim();
    if (!key) return "";
    if (urlCacheRef.current.has(key)) return urlCacheRef.current.get(key);
    const resolved = await resolveThemeSpriteUrl(key);
    urlCacheRef.current.set(key, resolved || "");
    return resolved || "";
  }, []);

  useEffect(() => {
    dispatch(setBackButtonUrl("/login"));
  }, [dispatch, status, user]);

  // If the same user revisits, fetch their existing best score/reportId once.
  useEffect(() => {
    if (!themeData || !user) return;
    const sessionId = user?.sessionId;
    const organizationId = user?.organizationId;
    const userId = user?.userId || user?.id;
    if (!sessionId || !organizationId || !userId || !adminToken) return;

    let cancelled = false;
    void (async () => {
      try {
        const reportData = await fetchReport({
          userId,
          sessionId,
          organizationId,
          token: adminToken,
        });
        if (cancelled) return;
        const score = Number(reportData?.report?.score);
        setExistingBestScore(Number.isFinite(score) ? score : null);
        const rid = reportData?.report?.reportId;
        setExistingReportId(rid != null && String(rid).trim() !== "" ? String(rid).trim() : null);
      } catch {
        // Non-blocking: gameplay should continue.
        if (!cancelled) {
          setExistingBestScore(null);
          setExistingReportId(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [adminToken, themeData, user]);

  useEffect(() => {
    dispatch(fetchThemeData({ themeId: null }));
  }, [dispatch]);

  useEffect(() => {
    if (themeStatus === "idle") {
      dispatch(fetchThemeData({ themeId: null }));
    }
  }, [dispatch, themeStatus]);

  useEffect(() => {
    const prevBodyOverflow = document.body.style.overflow;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyOverscroll = document.body.style.overscrollBehavior;
    const prevHtmlOverscroll = document.documentElement.style.overscrollBehavior;

 
    document.documentElement.style.overflow = "hidden";
    document.body.style.overscrollBehavior = "none";
    document.documentElement.style.overscrollBehavior = "none";

    return () => {
      document.body.style.overflow = prevBodyOverflow;
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.overscrollBehavior = prevBodyOverscroll;
      document.documentElement.style.overscrollBehavior = prevHtmlOverscroll;
    };
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobile(shouldUseMobileBackground());
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);

  useEffect(() => {
    themeDataRef.current = themeData;
  }, [themeData]);

  useEffect(() => {
    if (!themeData) return;

    const avgSpawn = (arr) => {
      if (!arr?.length) return 1;
      let sum = 0;
      for (const it of arr) sum += Math.max(0.05, Number(it.spawnRate) || 0);
      return sum / arr.length;
    };

    const swalMissingPaths = {
      icon: "warning",
      text: "The data are missing please add all the images to play the game",
      confirmButtonText: "OK",
      allowOutsideClick: false,
      allowEscapeKey: false,
    };
    const swalBugFiles = {
      icon: "warning",
      text: "Please add all the required files for good bugs and bad bugs from the admin panel.",
      confirmButtonText: "OK",
      allowOutsideClick: false,
      allowEscapeKey: false,
    };

    let cancelled = false;
    void (async () => {
      while (!cancelled) {
        const td = themeDataRef.current;
        if (!td) return;

        const missing = buildMissingThemeAssetList(td);
        if (missing.length) {
          setThemeAssetsOk(false);
          setAssetsReady(false);
          catalogRef.current = { good: [], bad: [], all: [] };
          await Swal.fire(swalMissingPaths);
          if (cancelled) return;
          try {
            await dispatch(fetchThemeData({ themeId: null })).unwrap();
          } catch {
            /* ignore */
          }
          if (cancelled) return;
          continue;
        }

        urlCacheRef.current = new Map();
        const nextCatalog = normalizeBugCatalog(td);
        catalogRef.current = nextCatalog;
        catalogSpawnMetaRef.current = {
          allAvg: avgSpawn(nextCatalog.all),
          badAvg: avgSpawn(nextCatalog.bad),
          goodAvg: avgSpawn(nextCatalog.good),
        };

        setThemeAssetsOk(false);
        setAssetsReady(false);

        let loadFailed = false;
        for (const b of nextCatalog.all) {
          if (cancelled) return;
          const gifUrl = await resolveCachedUrl(b.bugGif);
          const deathUrl = await resolveCachedUrl(b.deathImage);
          if (
            !gifUrl ||
            !(await checkImageExists(gifUrl)) ||
            !deathUrl ||
            !(await checkImageExists(deathUrl))
          ) {
            loadFailed = true;
            break;
          }
        }

        if (cancelled) return;

        if (loadFailed) {
          setThemeAssetsOk(false);
          setAssetsReady(false);
          catalogRef.current = { good: [], bad: [], all: [] };
          await Swal.fire(swalBugFiles);
          if (cancelled) return;
          try {
            await dispatch(fetchThemeData({ themeId: null })).unwrap();
          } catch {
            /* ignore */
          }
          if (cancelled) return;
          continue;
        }

        catalogRef.current = nextCatalog;
        setThemeAssetsOk(true);
        setAssetsReady(true);
        break;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [themeData, resolveCachedUrl, dispatch]);

  useEffect(() => {
    if (!showRules) return;
    let cancelled = false;
    void (async () => {
      const good = catalogRef.current.good;
      const bad = catalogRef.current.bad;
      const goodUrls = await Promise.all(good.map((b) => resolveCachedUrl(b.bugGif)));
      const badUrls = await Promise.all(bad.map((b) => resolveCachedUrl(b.bugGif)));
      if (!cancelled) {
        setRulesLegendUrls({
          good: good
            .map((b, idx) => ({ typeKey: b.typeKey || `good-${idx}`, src: goodUrls[idx] }))
            .filter((it) => Boolean(it.src)),
          bad: bad
            .map((b, idx) => ({ typeKey: b.typeKey || `bad-${idx}`, src: badUrls[idx] }))
            .filter((it) => Boolean(it.src)),
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showRules, themeData, resolveCachedUrl]);

  useEffect(() => {
    if (!themeData) {
      setTimeLeft(null);
      return;
    }

    const persisted = readPersistedTimer();
    const hasSameConfig =
      persisted?.configKey === timerConfigKey && typeof persisted?.timeLeft === "number";

    if (hasSameConfig) {
      // Don't use wall-clock deltas here (device local time can jump and instantly end the session).
      // We persist the latest `timeLeft` for this tab session and resume exactly from it.
      setTimeLeft(
        persisted.useCountdown
          ? Math.max(0, persisted.timeLeft)
          : Math.min(totalSeconds, Math.max(0, persisted.timeLeft)),
      );
      return;
    }

    setTimeLeft(useCountdown ? totalSeconds : 0);
  }, [themeData?.themename, themeData?.timer, themeData?.use_timeout, timerConfigKey, totalSeconds, useCountdown]);

  useEffect(() => {
    if (!themeData || timeLeft === null) return;
    writePersistedTimer({
      configKey: timerConfigKey,
      useCountdown,
      timeLeft,
      savedAt: Date.now(),
    });
  }, [themeData?.themename, timerConfigKey, timeLeft, useCountdown]);

  useEffect(() => {
    if (!hasThemeLoaded || !isPlaying || timeLeft === null) return undefined;

    const tick = useCountdown
      ? () => setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0))
      : () => setTimeLeft((prev) => (prev < totalSeconds ? prev + 1 : prev));

    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [hasThemeLoaded, isPlaying, timeLeft, totalSeconds, useCountdown]);

  useEffect(() => {
    const sessionId = String(user?.sessionId ?? "").trim().toLowerCase();
    if (!sessionId.startsWith("demo")) return undefined;
    let swalTimer;
    const redirectTimer = window.setTimeout(() => {
      swalTimer = window.setTimeout(() => {
        void Swal.fire({
          icon: "info",
          text: "Thank you for playing. Subscribe to any PLAN to play with your peers.",
          confirmButtonText: "OK",
          allowOutsideClick: false,
          allowEscapeKey: false,
        }).then(() => {
          window.location.assign("https://extramileplay.com/plans");
        });
      }, 400);
    }, 20000);
    return () => {
      window.clearTimeout(redirectTimer);
      if (swalTimer !== undefined) window.clearTimeout(swalTimer);
    };
  }, [user?.sessionId]);

  useEffect(() => {
    hitAudioPrimeGenRef.current += 1;
    hitAudioPrimedRef.current = false;
    if (hitAudioRef.current) {
      try {
        hitAudioRef.current.pause();
        hitAudioRef.current.src = "";
      } catch {
        /* ignore */
      }
      hitAudioRef.current = null;
    }
  }, [impactSoundSrc]);

  useEffect(() => {
    if (bgMusicRef.current) {
      bgMusicRef.current.pause();
      bgMusicRef.current = null;
    }
    if (!bgMusicSrc || !soundEnabledFromTheme(themeData)) return undefined;

    const audio = new Audio(bgMusicSrc);
    audio.loop = true;
    audio.volume = 0.28;
    if ("playsInline" in audio) audio.playsInline = true;
    bgMusicRef.current = audio;
    if (isPlaying) {
      void audio.play().catch(() => {});
    }
    return () => {
      audio.pause();
      if (bgMusicRef.current === audio) bgMusicRef.current = null;
    };
  }, [bgMusicSrc, themeData, isPlaying]);

  useEffect(() => {
    if (bgMusicRef.current) {
      if (isPlaying) void bgMusicRef.current.play().catch(() => {});
      else bgMusicRef.current.pause();
    }
  }, [isPlaying]);

  useEffect(() => {
    if (gamePageBgUrl) {
      document.body.style.backgroundImage = cssBackgroundImageUrl(gamePageBgUrl);
      document.body.style.backgroundPosition = "center";
      document.body.style.backgroundRepeat = "no-repeat";
      document.body.style.backgroundSize = "100% 100%";
      document.body.style.backgroundAttachment = "fixed";
    }

    return () => {
      document.body.style.removeProperty("background-image");
      document.body.style.removeProperty("background-position");
      document.body.style.removeProperty("background-repeat");
      document.body.style.removeProperty("background-size");
      document.body.style.removeProperty("background-attachment");
    };
  }, [gamePageBgUrl]);

  useEffect(
    () => () => {
      if (hitAudioRef.current) {
        hitAudioRef.current.pause();
        hitAudioRef.current = null;
      }
      hitAudioPrimedRef.current = false;
      Object.values(hitPointsTimersRef.current).forEach((timer) => window.clearTimeout(timer));
      hitPointsTimersRef.current = {};
      if (popupTimersRef.current.hide) window.clearTimeout(popupTimersRef.current.hide);
      if (popupTimersRef.current.onFire) window.clearTimeout(popupTimersRef.current.onFire);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    },
    [],
  );

  const showCenterPopup = useCallback((src) => {
    if (!src) return;
    if (popupTimersRef.current.hide) window.clearTimeout(popupTimersRef.current.hide);
    const id = `cp-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    setCenterPopup({ id, src });
    popupTimersRef.current.hide = window.setTimeout(() => {
      setCenterPopup(null);
      popupTimersRef.current.hide = 0;
    }, 900);
  }, []);

  const getHitAudio = () => {
    if (!impactSoundSrc) return null;
    if (!hitAudioRef.current) {
      const audio = new Audio();
      audio.preload = "auto";
      if ("playsInline" in audio) audio.playsInline = true;
      audio.volume = 1;
      audio.src = impactSoundSrc;
      audio.load();
      hitAudioRef.current = audio;
    }
    return hitAudioRef.current;
  };

  const playOscillatorHitFallback = () => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      if (!hitBeepCtxRef.current) hitBeepCtxRef.current = new Ctx();
      const ctx = hitBeepCtxRef.current;
      const beep = () => {
        try {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = "square";
          osc.frequency.value = 440;
          gain.gain.value = 0.5;
          osc.connect(gain);
          gain.connect(ctx.destination);
          const now = ctx.currentTime;
          osc.start(now);
          osc.stop(now + 0.15);
        } catch {
          /* ignore */
        }
      };
      if (ctx.state === "suspended") {
        void ctx.resume().then(beep);
      } else {
        beep();
      }
    } catch {
      /* ignore */
    }
  };

  const unlockHitAudio = () => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (Ctx && !hitBeepCtxRef.current) hitBeepCtxRef.current = new Ctx();
      if (hitBeepCtxRef.current?.state === "suspended") {
        void hitBeepCtxRef.current.resume();
      }

      if (!impactSoundSrc) return;
      const audio = getHitAudio();
      if (!audio) return;
      if (hitAudioPrimedRef.current) return;
      hitAudioPrimedRef.current = true;

      const prevVol = audio.volume;
      const primeGen = hitAudioPrimeGenRef.current;
      audio.volume = 0.001;
      audio.currentTime = 0;
      const playPromise = audio.play();
      if (playPromise && typeof playPromise.then === "function") {
        playPromise
          .then(() => {
            if (hitAudioPrimeGenRef.current !== primeGen) return;
            audio.pause();
            audio.currentTime = 0;
            audio.volume = prevVol;
          })
          .catch(() => {
            hitAudioPrimedRef.current = false;
            audio.volume = prevVol;
          });
      }
    } catch {
      hitAudioPrimedRef.current = false;
    }
  };

  const playHitSound = () => {
    if (!soundEnabledFromTheme(themeData)) return;
    if (!impactSoundSrc) {
      playOscillatorHitFallback();
      return;
    }

    const playSrc = (src, allowLocalRetry) => {
      const audio = new Audio(src);
      audio.volume = 1;
      const p = audio.play();
      if (p && typeof p.then === "function") {
        p.catch(() => {
          if (allowLocalRetry && src !== localImpactSound) playSrc(localImpactSound, false);
          else playOscillatorHitFallback();
        });
      }
    };

    playSrc(impactSoundSrc, impactSoundSrc !== localImpactSound);
  };

  const triggerShake = () => {
    setScreenShake(true);
    window.setTimeout(() => setScreenShake(false), SHAKE_MS);
  };

  const triggerComboPulse = () => {
    setComboPulse(true);
    window.setTimeout(() => setComboPulse(false), COMBO_PULSE_MS);
  };

  const mountBugDom = (bug) => {
    const playfield = playfieldRef.current;
    if (!playfield) return;
    const wrap = document.createElement("div");
    wrap.className = "game-bug";
    wrap.dataset.bugId = String(bug.id);
    const img = document.createElement("img");
    img.className = "game-bug-sprite";
    img.draggable = false;
    img.alt = "";
    img.src = bug.phase === "falling" ? bug.deathUrl : bug.liveUrl;
    wrap.appendChild(img);
    playfield.appendChild(wrap);
    bug.dom = { wrap, img };
  };

  const unmountBugDom = (bug) => {
    if (bug.dom?.wrap?.parentNode) {
      bug.dom.wrap.parentNode.removeChild(bug.dom.wrap);
    }
    bug.dom = null;
  };

  const layoutBugDom = (bug, w, h) => {
    if (!bug.dom?.wrap) return;
    const px = bug.nx * w;
    const py = bug.ny * h;
    const flip = bug.flipX ? -1 : 1;
    bug.dom.wrap.style.transform = `translate(${px}px, ${py}px) translate(-50%, -50%) scale(${bug.size * flip}, ${bug.size})`;
    const depth = Math.round(clamp(bug.ny, -1, 3) * 1000);
    bug.dom.wrap.style.zIndex = String(1000 + depth * 2 + bug.id);
  };

  const spawnBug = (forcedKind = null) => {
    const playfield = playfieldRef.current;
    const session = sessionRef.current;
    const catalog = catalogRef.current;
    const all = forcedKind === "bad" ? catalog.bad : forcedKind === "good" ? catalog.good : catalog.all;
    if (!playfield || !all.length || session.bugs.size >= MAX_LIVE_BUGS) return;

    const def = pickWeightedBug(Math.random, all);
    if (!def) return;

    const rng = Math.random;
    const moveType = def.bugType === "walking" ? "walking" : "flying";
    const laneBands = [0.82, 0.88, 0.94];
    const laneBase = moveType === "walking" ? laneBands[Math.floor(rng() * laneBands.length)] : null;
    // Flying bugs should traverse the play area in both directions.
    // Pick left->right or right->left and mirror-flip sprite for right->left.
    const flyDir = moveType === "flying" ? (rng() < 0.5 ? 1 : -1) : 1; // 1 => L->R, -1 => R->L
    const walkDir = moveType === "walking" ? (rng() < 0.5 ? 1 : -1) : 1;
    const edge =
      moveType === "walking"
        ? walkDir === 1
          ? 3 // left edge
          : 1 // right edge
        : flyDir === 1
          ? 3 // left edge
          : 1; // right edge
    let nx;
    let ny;
    const m = SPAWN_OFF_MARGIN * 0.85;
    if (edge === 0) {
      nx = rng();
      ny = -m;
    } else if (edge === 1) {
      nx = 1 + m;
      ny = moveType === "walking" ? laneBase + (rng() - 0.5) * 0.03 : rng();
    } else if (edge === 2) {
      nx = rng();
      ny = 1 + m;
    } else {
      nx = -m;
      ny =
        moveType === "walking"
          ? laneBase + (rng() - 0.5) * 0.03
          : 0.18 + rng() * 0.64;
    }

    // Multiple "air lanes" for flying so paths vary but remain readable.
    const flyLaneBands = [0.22, 0.32, 0.42, 0.52, 0.62, 0.72, 0.82];
    if (moveType === "flying") {
      ny = flyLaneBands[Math.floor(rng() * flyLaneBands.length)] + (rng() - 0.5) * 0.035;
    }

    const tx =
      moveType === "walking"
        ? walkDir === 1
          ? 1 + m
          : -m
        : flyDir === 1
          ? 1 + m
          : -m;
    const ty = moveType === "walking" ? ny : ny;
    let dx = tx - nx;
    let dy = ty - ny;
    const len = Math.hypot(dx, dy) || 1;
    dx /= len;
    dy /= len;
    const speedScale = (def.speed / 3.2) * 0.22;
    const walkSpeed = moveType === "walking" ? Math.max(0.085, speedScale * 1.05) : 0;

    // Avoid spawning too close to existing live bugs (reduces overlap).
    // Try a couple times for walkers (common overlap case).
    const tryCount = moveType === "walking" ? 6 : 2;
    for (let attempt = 0; attempt < tryCount; attempt += 1) {
      let ok = true;
      session.bugs.forEach((b) => {
        if (!ok || b.phase !== "live") return;
        const dxN = nx - b.nx;
        const dyN = ny - b.ny;
        const d = Math.hypot(dxN, dyN);
        if (moveType === "walking" && b.moveType === "walking") {
          if (Math.abs(dyN) < 0.05 && Math.abs(dxN) < 0.18) ok = false;
        } else if (d < 0.12) {
          ok = false;
        }
      });
      if (ok) break;
      if (moveType === "walking") {
        ny = laneBands[Math.floor(rng() * laneBands.length)] + (rng() - 0.5) * 0.03;
        nx = walkDir === 1 ? -m : 1 + m;
      } else {
        nx = rng();
        ny = rng();
      }
    }

    const bug = {
      id: session.nextBugId,
      kind: def.kind,
      typeKey: def.typeKey,
      moveType,
      points: def.points,
      nx,
      ny,
      vx: moveType === "walking" ? walkDir * walkSpeed : Math.max(0.09, speedScale * 1.05),
      vy: moveType === "walking" ? 0 : (rng() - 0.5) * 0.03,
      curvePhase: rng() * Math.PI * 2,
      curveOmega: 2.2 + rng() * 2.4,
      curveAmp:
        moveType === "walking"
          ? (0.08 + rng() * 0.08) * (def.speed / 4.5) * 0.7
          : (0.12 + rng() * 0.18) * (def.speed / 4.5) * 0.7,
      age: 0,
      entryEdge: edge,
      exitEdge: moveType === "flying" ? 1 : null,
      exitAfter: moveType === "flying" ? 0 : 0,
      exiting: moveType === "flying",
      // Mirror sprites when moving right -> left (works for both walking and flying).
      flipX: false,
      size: clamp(0.72 + (def.speed / 12) * 0.38 + (def.kind === "bad" ? 0.06 : 0), 0.62, 1.28),
      phase: "live",
      fallVy: 0,
      liveUrl: urlCacheRef.current.get(def.bugGif) || "",
      deathUrl: def.deathImage
        ? urlCacheRef.current.get(def.deathImage) || urlCacheRef.current.get(def.bugGif) || ""
        : urlCacheRef.current.get(def.bugGif) || "",
      laneY: moveType === "walking" ? ny : null,
      flyCenterY: moveType === "flying" ? ny : null,
      dom: null,
    };
    if (moveType === "flying") {
      // Ensure horizontal motion matches intended direction.
      bug.vx = Math.sign(flyDir) * Math.max(0.09, speedScale * 1.05);
      bug.flipX = flyDir === -1;
    } else {
      bug.flipX = bug.vx < 0;
    }
    if (!bug.liveUrl) return;

    session.nextBugId += 1;
    session.bugs.set(bug.id, bug);
    mountBugDom(bug);
    const rect = playfield.getBoundingClientRect();
    layoutBugDom(bug, rect.width, rect.height);
  };

  const removeBug = (id) => {
    const session = sessionRef.current;
    const bug = session.bugs.get(id);
    if (!bug) return;
    unmountBugDom(bug);
    session.bugs.delete(id);
  };

  const findTopBugAtClientPoint = (clientX, clientY) => {
    const playfield = playfieldRef.current;
    if (!playfield) return null;
    const rect = playfield.getBoundingClientRect();
    if (
      clientX < rect.left ||
      clientX > rect.right ||
      clientY < rect.top ||
      clientY > rect.bottom
    ) {
      return null;
    }
    const px = (clientX - rect.left) / Math.max(1, rect.width);
    const py = (clientY - rect.top) / Math.max(1, rect.height);
    const session = sessionRef.current;
    let best = null;
    let bestId = -1;
    const aspect = rect.width / Math.max(1, rect.height);
    session.bugs.forEach((bug) => {
      if (bug.phase !== "live") return;
      const dx = px - bug.nx;
      const dy = py - bug.ny;
      const dyN = dy / Math.max(0.25, aspect);
      const dist = Math.hypot(dx, dyN);
      const r = 0.09 * bug.size;
      if (dist <= r && bug.id > bestId) {
        best = bug;
        bestId = bug.id;
      }
    });
    return best;
  };

  const addHitPopup = (clientX, clientY, text, kind) => {
    const popupId = `p-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    setHitPointPopups((prev) => [...prev, { id: popupId, x: clientX, y: clientY, text, kind }]);
    hitPointsTimersRef.current[popupId] = window.setTimeout(() => {
      setHitPointPopups((prev) => prev.filter((item) => item.id !== popupId));
      delete hitPointsTimersRef.current[popupId];
    }, FLOAT_POINTS_MS);
  };

  const spawnDebrisBurst = (clientX, clientY) => {
    const host = playfieldRef.current || document.body;
    for (let i = 0; i < 9; i += 1) {
      const bit = document.createElement("span");
      bit.className = "game-debris";
      const ang = (Math.PI * 2 * i) / 9 + Math.random() * 0.4;
      const dist = 24 + Math.random() * 38;
      bit.style.setProperty("--dx", `${Math.cos(ang) * dist}px`);
      bit.style.setProperty("--dy", `${Math.sin(ang) * dist}px`);
      bit.style.left = `${clientX}px`;
      bit.style.top = `${clientY}px`;
      host.appendChild(bit);
      window.setTimeout(() => {
        if (bit.parentNode) bit.parentNode.removeChild(bit);
      }, 600);
    }
  };

  const spawnSmashBurst = (clientX, clientY) => {
    const list = splashListRef.current;
    if (list.length) {
      const url = list[Math.floor(Math.random() * list.length)];
      const el = document.createElement("img");
      el.className = "game-smash-splash";
      el.style.left = `${clientX}px`;
      el.style.top = `${clientY}px`;
      el.draggable = false;
      el.onerror = () => {
        if (el.parentNode) el.parentNode.removeChild(el);
        spawnDebrisBurst(clientX, clientY);
      };
      el.src = url;
      document.body.appendChild(el);
      window.setTimeout(() => {
        if (el.parentNode) el.parentNode.removeChild(el);
      }, 520);
      return;
    }
    spawnDebrisBurst(clientX, clientY);
  };

  const applyHit = (bug, clientX, clientY) => {
    const session = sessionRef.current;
    if (bug.phase !== "live") return;

    // Take the bug out of the "live" hit-test path immediately so concurrent
    // pointer/touch events (e.g. two fingers) cannot apply two hits to one bug.
    bug.phase = "falling";
    bug.fallVy = 0.15;

    const isStreakHit = bug.kind === "bad";
    const prevCombo = session.combo;
    let nextCombo = prevCombo;
    const now = Date.now();

    if (isStreakHit) {
      nextCombo = prevCombo + 1;
      session.combo = nextCombo;
      session.lastHitWallMs = now;
      if (prevCombo === 0) {
        session.chainWallStartMs = now;
      }
    } else {
      // Good bug hits should not build streak; reset combo.
      session.combo = 0;
      nextCombo = 0;
      session.chainWallStartMs = 0;
    }

    const mult = isStreakHit ? comboMultiplierForStreak(nextCombo) : 1;
    const hitScore = Math.round(bug.points * mult * 100) / 100;
    session.sumHitScores += hitScore;
    session.multSum += mult;

    if (isStreakHit && nextCombo % 10 === 0) {
      session.comboBonusCount += 1;
      session.comboBonusPoints += 3;
    }

    if (
      session.chainWallStartMs > 0 &&
      Date.now() - session.chainWallStartMs >= 15000
    ) {
      session.streakBonusCount += 1;
      session.streakBonusPoints += 10;
      session.chainWallStartMs = now;
    }

    session.maxCombo = Math.max(session.maxCombo, nextCombo);
    session.successfulHits += 1;
    if (bug.kind === "good") {
      session.goodHits += 1;
    } else {
      session.badHits += 1;
    }
    session.hitsByBugType[bug.typeKey] = (session.hitsByBugType[bug.typeKey] || 0) + 1;

    const finalScore =
      session.sumHitScores + session.comboBonusPoints + session.streakBonusPoints;
    setScore(finalScore);
    setCombo(nextCombo);
    setGoodHits(session.goodHits);
    setBadHits(session.badHits);
    setMaxComboHud(session.maxCombo);

    if (isStreakHit) {
      if (nextCombo === 3) showCenterPopup(popupGood);
      else if (nextCombo === 5) showCenterPopup(popupNice);
      else if (nextCombo === 10) {
        showCenterPopup(popupOnFire);
        setOnFirePulse(true);
        if (popupTimersRef.current.onFire) window.clearTimeout(popupTimersRef.current.onFire);
        popupTimersRef.current.onFire = window.setTimeout(() => {
          setOnFirePulse(false);
          popupTimersRef.current.onFire = 0;
        }, 650);
      } else if (nextCombo === 15) {
        showCenterPopup(popupInsane);
      }
    }

    const sign = hitScore > 0 ? "+" : "";
    addHitPopup(
      clientX,
      clientY,
      `${sign}${hitScore}`,
      hitScore >= 0 ? "good" : "bad",
    );

    // Show splash first (briefly), then switch bug sprite to death image as it falls.
    spawnSmashBurst(clientX, clientY);

    playHitSound();
    triggerShake();
    if (isStreakHit) triggerComboPulse();

    const deathSrc = bug.deathUrl || bug.liveUrl;
    window.setTimeout(() => {
      const stillThere = sessionRef.current.bugs.get(bug.id) === bug;
      if (!stillThere) return;
      if (bug.dom?.img) bug.dom.img.src = deathSrc;
    }, 90);
  };

  const gameLoop = (t) => {
    if (!playActiveRef.current) return;

    const lt = lastFrameRef.current ?? t;
    const dt = clamp((t - lt) / 1000, 0, 0.052);
    lastFrameRef.current = t;

    const session = sessionRef.current;
    const wall = Date.now();
    // Smash streak should only reset when the player hits a good bug (handled in `applyHit`).

    const playfield = playfieldRef.current;
    const rect = playfield?.getBoundingClientRect();
    const w = rect?.width || 1;
    const h = rect?.height || 1;
    // Bug movement uses normalized coordinates (0..1 across the playfield).
    // On narrow portrait screens, the same normalized velocity becomes fewer pixels/sec,
    // which feels slower than desktop. Scale motion so pixel speed stays consistent.
    const speedMul = clamp(900 / Math.max(1, w), 1, 2.6);

    const elapsedWall = Math.max(0, wall - session.sessionWallStartMs);
    const intensity = elapsedWall >= 30000 ? 1.15 : 1;
    const { all } = catalogRef.current;
    const spawnMeta = catalogSpawnMetaRef.current;
    // Use theme bug `spawn_rate` to scale overall spawn frequency (not only selection weighting).
    const allSpawnScale = clamp(spawnMeta?.allAvg ?? 1, 0.25, 3);
    if (all.length && session.bugs.size < MAX_LIVE_BUGS) {
      session.spawnAcc +=
        dt * 0.62 * intensity * allSpawnScale * (0.55 + Math.min(all.length, 12) / 20);
      while (session.spawnAcc >= 1 && session.bugs.size < MAX_LIVE_BUGS) {
        session.spawnAcc -= 1;
        spawnBug();
      }
    }

    // Keep bad bugs showing repeatedly (avoid long empty gaps).
    const badLiveCount = Array.from(session.bugs.values()).filter((b) => b.phase === "live" && b.kind === "bad").length;
    if (catalogRef.current.bad.length && badLiveCount < 3 && session.bugs.size < MAX_LIVE_BUGS) {
      // add extra pressure based on how "empty" bad bugs are
      const missing = 3 - badLiveCount;
      const badSpawnScale = clamp(spawnMeta?.badAvg ?? 1, 0.25, 3);
      session.spawnAcc += dt * 0.35 * missing * badSpawnScale;
      while (session.spawnAcc >= 1 && session.bugs.size < MAX_LIVE_BUGS) {
        session.spawnAcc -= 1;
        spawnBug("bad");
      }
    }

    const toRemove = [];
    session.bugs.forEach((bug) => {
      if (bug.phase === "live") {
        bug.age += dt;
        if (bug.moveType === "walking") {
          // Keep walkers in a bottom lane and move mostly sideways.
          const laneCenter = typeof bug.laneY === "number" ? bug.laneY : 0.9;
          const wobble = Math.sin(bug.curvePhase + bug.age * (bug.curveOmega * 0.8)) * bug.curveAmp;
          bug.nx += bug.vx * dt * speedMul;
          // stay around lane center (no drifting to same stuck spot)
          bug.ny = clamp(laneCenter + wobble, 0.80, 0.98);

          if (bug.nx < -DESPAWN_OFF_MARGIN || bug.nx > 1 + DESPAWN_OFF_MARGIN) {
            session.escapedBugs += 1;
            toRemove.push(bug.id);
          }
        } else {
          // Flying bugs: keep a clear left -> right traversal.
          // Add gentle vertical wobble but do not despawn due to top/bottom drift.
          const wobbleAy =
            Math.cos(bug.curvePhase * 0.8 + bug.age * bug.curveOmega * 0.9) * bug.curveAmp * 0.55;
          bug.vy += wobbleAy * dt;

          // Maintain forward motion.
          const vxSign = bug.vx === 0 ? (bug.flipX ? -1 : 1) : Math.sign(bug.vx);
          bug.vx = vxSign * Math.max(Math.abs(bug.vx), 0.085);
          bug.vx *= Math.pow(0.996, dt * 60);
          bug.vy *= Math.pow(0.992, dt * 60);

          bug.nx += bug.vx * dt * speedMul;
          bug.ny += bug.vy * dt * speedMul;

          // Bounce softly within vertical bounds to prevent mid-screen "vanish" via top/bottom exit.
          if (bug.ny < 0.06) {
            bug.ny = 0.06;
            bug.vy = Math.abs(bug.vy) * 0.7 + 0.02;
          } else if (bug.ny > 0.94) {
            bug.ny = 0.94;
            bug.vy = -Math.abs(bug.vy) * 0.7 - 0.02;
          }

          // Despawn only when it truly leaves left/right edges (in its direction of travel).
          if (bug.nx < -DESPAWN_OFF_MARGIN || bug.nx > 1 + DESPAWN_OFF_MARGIN) {
            session.escapedBugs += 1;
            toRemove.push(bug.id);
          }
        }
      } else {
        bug.fallVy += FALL_ACCEL * dt;
        bug.ny += bug.fallVy * dt;
        if (bug.ny > 1.35) {
          toRemove.push(bug.id);
        }
      }
      layoutBugDom(bug, w, h);
    });

    toRemove.forEach((id) => removeBug(id));

    rafRef.current = requestAnimationFrame(gameLoop);
  };

  const teardownPlayfieldBugs = () => {
    const session = sessionRef.current;
    session.bugs.forEach((bug) => unmountBugDom(bug));
    session.bugs.clear();
    session.spawnAcc = 0;
  };

  const startEngine = () => {
    if (!assetsReady || !catalogRef.current.all.length) return;
    teardownPlayfieldBugs();
    sessionRef.current = createSessionState();
    sessionRef.current.sessionWallStartMs = Date.now();
    lastFrameRef.current = null;
    playActiveRef.current = true;
    setScore(0);
    setCombo(0);
    setGoodHits(0);
    setBadHits(0);
    setMaxComboHud(0);
    setHitPointPopups([]);
    Object.values(hitPointsTimersRef.current).forEach((tid) => window.clearTimeout(tid));
    hitPointsTimersRef.current = {};
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(gameLoop);
  };

  useEffect(() => {
    if (isPlaying && assetsReady) {
      gameOverHandledRef.current = false;
      startEngine();
    } else {
      playActiveRef.current = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      teardownPlayfieldBugs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- engine restart is intentional on play gate
  }, [isPlaying, assetsReady]);

  const buildExtraStatsPayload = (session, sessionDurationSec) => {
    const acc =
      session.totalTaps > 0 ? (session.successfulHits / session.totalTaps) * 100 : 0;
    const avgMult =
      session.successfulHits > 0 ? session.multSum / session.successfulHits : 0;
    return {
      finalScore: session.sumHitScores + session.comboBonusPoints + session.streakBonusPoints,
      totalHits: session.successfulHits,
      hitsByBugType: session.hitsByBugType,
      maxCombo: session.maxCombo,
      comboBonusCount: session.comboBonusCount,
      comboBonusPoints: session.comboBonusPoints,
      streakBonusCount: session.streakBonusCount,
      streakBonusPoints: session.streakBonusPoints,
      escapedBugs: session.escapedBugs,
      totalTaps: session.totalTaps,
      successfulHits: session.successfulHits,
      accuracyPercentage: Math.round(acc * 100) / 100,
      averageMultiplier: Math.round(avgMult * 1000) / 1000,
      sessionDuration: sessionDurationSec,
      sumHitScores: session.sumHitScores,
    };
  };

  const persistSession = useCallback(async () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const sessionId = user?.sessionId;
    const organizationId = user?.organizationId;
    const userId = user?.userId || user?.id;
    const name = user?.name;
    const email = user?.email;
    const role = user?.role;

    if (!sessionId || !organizationId || !userId || !name || !role) {
      throw new Error("Missing user session for save");
    }

    const session = sessionRef.current;
    const wallDur = session.sessionWallStartMs
      ? Math.floor((Date.now() - session.sessionWallStartMs) / 1000)
      : 0;
    // Use the configured countdown duration for saved "time".
    // Wall-clock duration can be a couple seconds short due to interval/RAF drift near 0.
    const sessionDurationSec = useCountdown
      ? Math.max(1, totalSeconds || 1)
      : Math.max(1, wallDur || elapsedSeconds || 1);
    const finalScore = session.sumHitScores + session.comboBonusPoints + session.streakBonusPoints;
    const reportScore = Math.round(finalScore);
    const extraStats = {
      ...buildExtraStatsPayload(session, sessionDurationSec),
      finalScore: reportScore,
    };

    const reportSignature = JSON.stringify({
      sessionId,
      organizationId,
      userId,
      timerConfigKey,
      role,
      finalScore: reportScore,
      time: sessionDurationSec,
    });
    if (readLastReportSignature() === reportSignature) {
      return { duplicate: true };
    }

    const formattedElapsedTime = `00:${String(sessionDurationSec).padStart(2, "0")}`;
    const reportData = {
      sessionId,
      role,
      token: user?.token || null,
      name,
      userId,
      gameId: user?.gameId || "",
      organizationId,
      points: reportScore,
      time: formattedElapsedTime,
      reportId: existingReportId || user?.reportId || null,
      ans: "",
    };

    // Only overwrite existing DB records if the new score is higher.
    if (existingBestScore != null && Number.isFinite(existingBestScore) && reportScore <= existingBestScore) {
      return { duplicate: true, skipped: true, reason: "score_not_higher" };
    }

    const sendReportResponse = await sendReport(reportData);
    console.log("sendReport response:", sendReportResponse);
    const resolvedReportId =
      extractReportIdFromResponse(sendReportResponse) ||
      extractReportIdFromResponse(sendReportResponse?.data) ||
      existingReportId ||
      user?.reportId ||
      null;
    console.log("Resolved reportId:", resolvedReportId);

    if (!backendUrl || !email) {
      throw new Error("Missing backend URL or email");
    }

    const token = `${sessionId}&${organizationId}`;
    const submitRes = await fetch(`${backendUrl}/submitReport`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        userid: userId,
        name,
        email,
        user_type: role || "ORG_USER",
        sessionId,
        organizationId,
        score: reportScore,
        time: sessionDurationSec,
        good: session.goodHits,
        bad: session.badHits,
        smashStreak: session.maxCombo,
        status: "completed",
        timeout: useCountdown ? 1 : 0,
        extraStats,
        reportId: resolvedReportId,
      }),
    });

    if (!submitRes.ok) {
      const errText = await submitRes.text().catch(() => "");
      throw new Error(errText || `submitReport failed (${submitRes.status})`);
    }

    writeLastReportSignature(reportSignature);
    setExistingBestScore(reportScore);
    if (resolvedReportId) setExistingReportId(String(resolvedReportId));
    return { duplicate: false };
  }, [
    elapsedSeconds,
    existingBestScore,
    existingReportId,
    timerConfigKey,
    useCountdown,
    user?.email,
    user?.gameId,
    user?.id,
    user?.name,
    user?.organizationId,
    user?.reportId,
    user?.role,
    user?.sessionId,
    user?.token,
    user?.userId,
  ]);

  const runEndgameSave = useCallback(async () => {
    if (gameOverHandledRef.current) return;
    gameOverHandledRef.current = true;
    playActiveRef.current = false;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    teardownPlayfieldBugs();

    setSaveState("saving");
    setSaveErrorDetail("");
    try {
      await persistSession();
      setSaveState("done");
      // Ensure a completed timer doesn't get restored on a future visit and immediately redirect again.
      clearPersistedTimer();
      navigate("/thank-you");
    } catch (e) {
      console.error(e);
      gameOverHandledRef.current = false;
      setSaveState("error");
      setSaveErrorDetail(e?.message || "Save failed");
    }
  }, [navigate, persistSession]);

  useEffect(() => {
    if (!hasThemeLoaded || !isSessionOver) return;
    void runEndgameSave();
  }, [hasThemeLoaded, isSessionOver, runEndgameSave]);

  const onPlayfieldPointerDown = (e) => {
    if (!playActiveRef.current) return;
    if (e.button !== 0 && e.pointerType === "mouse") return;
    unlockHitAudio();
    const session = sessionRef.current;
    session.totalTaps += 1;

    const bug = findTopBugAtClientPoint(e.clientX, e.clientY);
    if (bug) {
      applyHit(bug, e.clientX, e.clientY);
    }
  };

  return (
    <div
      className={`game-page ${onFirePulse ? "game-page--onfire game-page--firepulse" : ""}`}
    >
      {/* Softened copy of the body background so bugs stand out against busy/dark themes. */}
      {gamePageBgUrl ? (
        <div
          className="game-bg-blur"
          style={{ backgroundImage: cssBackgroundImageUrl(gamePageBgUrl) }}
          aria-hidden
        />
      ) : null}
      <button
        type="button"
        className="game-help-btn"
        onClick={() => setShowRules(true)}
        aria-label="Game rules"
        style={{
          background: themeData?.button_background
            ? themeData.button_background
            : `linear-gradient(180deg, ${themeData?.button_color || "#7c3aed"}, ${
                themeData?.button_color || "#7c3aed"
              })`,
          color: themeData?.button_text_color ? themeData.button_text_color : "#fff",
        }}
      >
        ?
      </button>

      <div
        className="game-hud"
        style={{
          background: themeData?.button_background
            ? themeData.button_background
            : `linear-gradient(180deg, ${themeData?.button_color || "#7c3aed"}, ${
                themeData?.button_color || "#7c3aed"
              })`,
          color: themeData?.button_text_color ? themeData.button_text_color : "#fff",
        }}
      >
        <div className="game-stat" style={{ color: themeData?.button_text_color }}>
          <span style={{ color: themeData?.button_text_color }}>
            {useCountdown ? "Time left" : "Time"}
          </span>
          <strong>{timerReady ? formatClock(timeLeft) : "—:—"}</strong>
        </div>
        <div className="game-stat">
          <span style={{ color: themeData?.button_text_color }}>Score</span>
          <strong style={{ color: themeData?.button_text_color }}>{score}</strong>
        </div>
      </div>

      {showRules && (
        <div
          className="game-rules-overlay"
          role="dialog"
          aria-modal="true"
          onPointerDown={(e) => {
            if (e.target === e.currentTarget) setShowRules(false);
          }}
        >
          <div
            className="game-rules-card"
            style={{
              background: themeData?.button_background
                ? themeData.button_background
                : themeData?.button_color || undefined,
            }}
          >
            <div className="game-rules-header">
              <h2 style={{ color: themeData?.button_text_color || undefined }}>
                How To Play
              </h2>
              <button type="button" className="game-rules-close" onClick={() => setShowRules(false)}>
                ✕
              </button>
            </div>

            {(catalogRef.current.good.length > 0 || catalogRef.current.bad.length > 0) && (
              <div className="game-rules-legend">
                <div className="game-rules-legend-item game-rules-legend-item--bad">
                  <div className="game-rules-legend-title">Bad bugs (hit these)</div>
                  <div className="game-rules-legend-icons" aria-hidden>
                    {rulesLegendUrls.bad.map((b) => (
                      <img
                        key={b.typeKey}
                        className="game-rules-legend-icon"
                        src={b.src}
                        alt=""
                        draggable={false}
                      />
                    ))}
                  </div>
                </div>

                <div className="game-rules-legend-item game-rules-legend-item--good">
                  <div className="game-rules-legend-title">Good bugs (avoid)</div>
                  <div className="game-rules-legend-icons" aria-hidden>
                    {rulesLegendUrls.good.map((b) => (
                      <img
                        key={b.typeKey}
                        className="game-rules-legend-icon"
                        src={b.src}
                        alt=""
                        draggable={false}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}
            {Array.isArray(themeData?.rules) && themeData.rules.length ? (
              <ul className="game-rules-list">
                {themeData.rules.filter(Boolean).map((rule, idx) => (
                  <li
                    key={`${idx}-${String(rule).slice(0, 12)}`}
                    style={{ color: themeData?.button_text_color || undefined }}
                  >
                    {rule}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="game-rules-empty">No rules configured for this theme.</p>
            )}
          </div>
        </div>
      )}

      <div className={`game-scene ${screenShake ? "game-scene--shake" : ""}`}>
        <div
          ref={playfieldRef}
          className="game-playfield"
          onPointerDown={onPlayfieldPointerDown}
          role="presentation"
        />

        <div
          className={`game-streak-screen ${combo >= 3 ? "game-streak-screen--glow" : ""} ${
            combo >= 5 ? "game-streak-screen--aura" : ""
          } ${onFirePulse ? "game-streak-screen--onfire" : ""} ${
            combo >= 15 ? "game-streak-screen--insane" : ""
          }`}
          aria-hidden
        />
        <div className={`game-fire-overlay ${onFirePulse ? "is-active" : ""}`} aria-hidden />

        {hitPointPopups.map((popup) => (
          <span
            key={popup.id}
            className={`game-point game-point-${popup.kind}`}
            style={{
              left: popup.x,
              top: popup.y,
            }}
            aria-hidden
          >
            {popup.text}
          </span>
        ))}

        {centerPopup && (
          <img
            key={centerPopup.id}
            className="game-center-popup"
            src={centerPopup.src}
            alt=""
            aria-hidden
            draggable={false}
          />
        )}
      </div>

      {saveState === "saving" && (
        <div className="game-end-overlay" role="status">
          <div className="game-end-card">Saving your score…</div>
        </div>
      )}

      {saveState === "error" && (
        <div className="game-end-overlay">
          <div className="game-end-card game-end-card--error">
            <h2>Could not save</h2>
            <p>{saveErrorDetail || "Please try again."}</p>
            <button
              type="button"
              onClick={() => {
                setSaveState("idle");
                void runEndgameSave();
              }}
            >
              Retry
            </button>
            <button type="button" className="game-end-secondary" onClick={() => navigate("/thank-you")}>
              Continue anyway
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Game;
